/**
 * PaymentsActivationBanner - Global FLOATING proactive banner for payments setup
 * 
 * Shows for contractor admin/owner when payments are not fully ACTIVE.
 * Uses /api/settings/payments/activation-status as single source of truth.
 * 
 * RULES:
 * - Show ONLY for contractor admin/owner roles (not technicians)
 * - Show ONLY when state is IN_PROGRESS or APPROVED_UNPAID
 * - Show on main function pages AND Settings pages
 * - Hide when ACTIVE or snoozed (15 MINUTE TTL - auto-returns, NEVER permanent)
 * - NO Close (X) button - contractors cannot permanently dismiss
 * - "Remind me later" snoozes for 15 minutes only
 * 
 * ROUTE SCOPE (show on these routes):
 * - /, /dashboard
 * - /jobs
 * - /customers
 * - /follow-ups
 * - /estimates
 * - /invoices
 * - /calendar
 * - /reports
 * - /settings (all tabs)
 * 
 * EXCLUDED:
 * - /email-marketing, /reviews, /review-analytics (marketing)
 * - /admin/* (admin pages)
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CreditCard, CheckCircle } from "phosphor-react";
import { Button } from "./ui/button";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Snooze duration: 15 MINUTES in milliseconds (short TTL - returns soon)
// This ensures contractors are frequently reminded to complete onboarding
const SNOOZE_DURATION_MS = 15 * 60 * 1000;  // 15 minutes

// V3 snooze key format - short TTL, no permanent dismiss
const SNOOZE_KEY_PREFIX = "fsp_payments_banner_snooze_v3_";

// Legacy keys to clean up (from old implementations) - includes v2 keys
const LEGACY_KEYS_TO_CLEAN = [
  "fsp_payments_banner_snooze_v2_",  // v2 format (24h TTL)
  "fsp_payments_banner_snooze_",     // Old format without version
  "payments_activation_banner_dismissed",
  "activation_banner_dismissed",
  "finix_banner_closed",
  "payments_banner_hide",
  "paymentsActivationBannerDismissed",
];

// Routes where banner should appear (main function pages + settings)
const ALLOWED_ROUTES = [
  "/",
  "/dashboard",
  "/jobs",
  "/customers",
  "/follow-ups",
  "/estimates",
  "/invoices",
  "/calendar",
  "/reports",
  "/settings",  // Added: Settings pages for merchant status visibility
];

/**
 * Clean up legacy dismissal keys that may be blocking banner
 * Run once on component mount
 * Cleans up: v1 keys, v2 keys (24h TTL), and other legacy patterns
 */
function cleanupLegacyDismissalKeys() {
  try {
    // Find and remove all legacy keys
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      
      // Check against legacy patterns (anything not v3)
      for (const legacyPrefix of LEGACY_KEYS_TO_CLEAN) {
        if (key.startsWith(legacyPrefix) && !key.includes("_v3_")) {
          keysToRemove.push(key);
          break;
        }
      }
    }
    
    // Remove legacy keys
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
      console.debug(`[PaymentsActivationBanner] Cleaned up legacy key: ${key}`);
    }
    
    if (keysToRemove.length > 0) {
      console.debug(`[PaymentsActivationBanner] Cleaned up ${keysToRemove.length} legacy dismissal keys`);
    }
  } catch (err) {
    // localStorage may be disabled - fail silently
    console.debug("[PaymentsActivationBanner] Could not clean up legacy keys:", err.message);
  }
}

/**
 * Get snooze key for tenant (v2 format)
 */
function getSnoozeKey(tenantId) {
  return `${SNOOZE_KEY_PREFIX}${tenantId}`;
}

/**
 * Check if banner is snoozed for tenant
 * Returns false (show banner) if:
 * - No snooze key exists
 * - Snooze has expired (TTL passed)
 */
function isSnoozed(tenantId) {
  if (!tenantId) return false;
  
  try {
    const snoozeUntil = localStorage.getItem(getSnoozeKey(tenantId));
    if (!snoozeUntil) return false;
    
    const snoozeTs = parseInt(snoozeUntil, 10);
    if (isNaN(snoozeTs)) return false;
    
    // TTL check - if expired, return false (show banner)
    const isStillSnoozed = Date.now() < snoozeTs;
    
    // If expired, clean up the key
    if (!isStillSnoozed) {
      localStorage.removeItem(getSnoozeKey(tenantId));
    }
    
    return isStillSnoozed;
  } catch {
    return false;
  }
}

/**
 * Set snooze for tenant (24 hours TTL - NEVER permanent)
 */
function setSnooze(tenantId) {
  if (!tenantId) return;
  
  try {
    const snoozeUntil = Date.now() + SNOOZE_DURATION_MS;
    localStorage.setItem(getSnoozeKey(tenantId), String(snoozeUntil));
  } catch {
    // localStorage may be full or disabled - fail silently
  }
}

/**
 * Check if current route is in the allowed list for banner display
 */
function isAllowedRoute(pathname) {
  // Normalize pathname
  const normalizedPath = pathname.toLowerCase().replace(/\/$/, "") || "/";
  
  // Check exact matches first
  if (ALLOWED_ROUTES.includes(normalizedPath)) {
    return true;
  }
  
  // Check if it's a subpath of allowed routes (e.g., /jobs/123)
  // But EXCLUDE marketing and admin routes
  const excludedPrefixes = [
    "/email-marketing",
    "/reviews",
    "/review-analytics",
    "/admin",
    "/platform-admin",
  ];
  
  for (const prefix of excludedPrefixes) {
    if (normalizedPath.startsWith(prefix)) {
      return false;
    }
  }
  
  // Allow subpaths of main routes (e.g., /customers/123)
  for (const route of ALLOWED_ROUTES) {
    if (route !== "/" && normalizedPath.startsWith(route)) {
      return true;
    }
  }
  
  return false;
}

export default function PaymentsActivationBanner({ user, tenant }) {
  // P0 DIAGNOSTIC: Log component mount with props
  console.log("[PaymentsActivationBanner] COMPONENT RENDER - props received:", {
    user: user ? { role: user.role, user_id: user.user_id, email: user.email } : 'undefined/null',
    tenant: tenant ? { tenant_id: tenant.tenant_id, company_name: tenant.company_name } : 'undefined/null',
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  const [activationStatus, setActivationStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [ctaLoading, setCtaLoading] = useState(false);
  const [ctaError, setCtaError] = useState(null);
  const fetchedRef = useRef(false);
  const cleanedUpRef = useRef(false);
  
  const tenantId = tenant?.tenant_id;
  
  // =========================================================================
  // LEGACY CLEANUP - Run once on first mount
  // =========================================================================
  useEffect(() => {
    if (cleanedUpRef.current) return;
    cleanedUpRef.current = true;
    cleanupLegacyDismissalKeys();
  }, []);
  
  // Check route allowlist
  const routeAllowed = isAllowedRoute(location.pathname);
  
  // =========================================================================
  // P0 DIAGNOSTIC: Get snooze info for logging
  // =========================================================================
  const getSnoozeInfo = () => {
    if (!tenantId) return { snoozed: false, snoozeKey: null, snoozeValue: null, snoozeExpiry: null };
    const snoozeKey = `fsp_payments_banner_snooze_v3_${tenantId}`;
    try {
      const snoozeValue = localStorage.getItem(snoozeKey);
      if (!snoozeValue) return { snoozed: false, snoozeKey, snoozeValue: null, snoozeExpiry: null };
      const snoozeTs = parseInt(snoozeValue, 10);
      const isStillSnoozed = Date.now() < snoozeTs;
      return { 
        snoozed: isStillSnoozed, 
        snoozeKey, 
        snoozeValue, 
        snoozeExpiry: new Date(snoozeTs).toISOString(),
        currentTime: new Date().toISOString()
      };
    } catch {
      return { snoozed: false, snoozeKey, snoozeValue: null, snoozeExpiry: null, error: 'localStorage access failed' };
    }
  };
  
  // Determine if banner should show based on state
  // P0 FIX: Removed broken role guard (user object undefined after Supabase migration)
  // Backend API handles authorization - only tenantId needed for frontend gating
  const shouldShowBanner = useCallback(() => {
    const snoozeInfo = getSnoozeInfo();
    
    // P0 DIAGNOSTIC: Log all input values at start of decision
    console.log("[PaymentsActivationBanner] DIAGNOSTIC - shouldShowBanner() called with:", {
      tenantId,
      pathname: location.pathname,
      routeAllowed,
      isLoading,
      isHidden,
      activationStatus: activationStatus ? {
        state: activationStatus.state,
        payments_usable: activationStatus.payments_usable,
        in_progress_sub_state: activationStatus.in_progress_sub_state,
        next_step_action: activationStatus.next_step?.action,
      } : null,
      snoozeInfo,
      userProp: user ? { role: user.role, user_id: user.user_id } : 'undefined/null',
      tenantProp: tenant ? { tenant_id: tenant.tenant_id } : 'undefined/null',
    });
    
    // Route gating - only show on main function pages
    if (!routeAllowed) {
      console.log("[PaymentsActivationBanner] EARLY RETURN: route not allowed:", location.pathname);
      return false;
    }
    
    // P0 FIX: Replace broken role guard with tenantId guard
    // Backend activation-status API handles authorization
    if (!tenantId) {
      console.log("[PaymentsActivationBanner] EARLY RETURN: no tenantId. tenant prop =", tenant);
      return false;
    }
    if (!activationStatus) {
      console.log("[PaymentsActivationBanner] EARLY RETURN: no activationStatus yet (API not fetched or failed)");
      return false;
    }
    if (isHidden) {
      console.log("[PaymentsActivationBanner] EARLY RETURN: isHidden=true (local state)");
      return false;
    }
    if (snoozeInfo.snoozed) {
      console.log("[PaymentsActivationBanner] EARLY RETURN: snoozed for tenant", tenantId, snoozeInfo);
      return false;
    }
    
    // CANONICAL CHECK: If payments are usable, don't show banner
    // This is the source of truth from backend - covers all edge cases
    if (activationStatus.payments_usable === true) {
      console.log("[PaymentsActivationBanner] EARLY RETURN: payments_usable=true (ACTIVE state)");
      return false;
    }
    
    const state = activationStatus.state;
    // Show for IN_PROGRESS, APPROVED_UNPAID, and APPROVED_MERCHANT_MISSING
    // NOT_STARTED is intentionally excluded to avoid being aggressive on day one
    // ACTIVE is excluded because payments_usable would be true
    const shouldShow = state === "IN_PROGRESS" || state === "APPROVED_UNPAID" || state === "APPROVED_MERCHANT_MISSING";
    
    if (!shouldShow) {
      console.log("[PaymentsActivationBanner] EARLY RETURN: state not in showable list. state =", state);
    } else {
      console.log("[PaymentsActivationBanner] WILL SHOW: state =", state, ", shouldShow =", shouldShow);
    }
    
    return shouldShow;
  }, [activationStatus, isHidden, tenantId, routeAllowed, location.pathname, user, tenant, isLoading]);
  
  // =========================================================================
  // FETCH ACTIVATION STATUS
  // =========================================================================
  
  const fetchActivationStatus = useCallback(async () => {
    // P0 DIAGNOSTIC: Log fetch attempt
    console.log("[PaymentsActivationBanner] FETCH: Starting activation-status fetch", { tenantId });
    
    // P0 FIX: Removed isAdminOrOwner check - backend handles authorization
    if (!tenantId) {
      console.log("[PaymentsActivationBanner] FETCH SKIPPED: no tenantId");
      return;
    }
    
    setIsLoading(true);
    try {
      // P0 FIX: Include Bearer token for auth (same pattern as Dashboard.js, AppLayout.js)
      // After Google Auth cutover, Bearer token is the reliable auth path
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      console.log("[PaymentsActivationBanner] FETCH: Calling API", { 
        hasToken: !!token, 
        tokenLength: token?.length 
      });
      
      const response = await fetch(`${API}/settings/payments/activation-status`, {
        credentials: "include",
        headers,
      });
      
      if (!response.ok) {
        // Log the failure for debugging
        console.log("[PaymentsActivationBanner] FETCH FAILED: HTTP", response.status, response.statusText);
        // Reset fetchedRef to allow retry on next mount/re-render
        fetchedRef.current = false;
        setActivationStatus(null);
        return;
      }
      
      const data = await response.json();
      console.log("[PaymentsActivationBanner] FETCH SUCCESS:", {
        state: data.state,
        payments_usable: data.payments_usable,
        in_progress_sub_state: data.in_progress_sub_state,
        next_step_action: data.next_step?.action,
      });
      setActivationStatus(data);
      // Only mark as fetched after SUCCESSFUL fetch
      fetchedRef.current = true;
    } catch (err) {
      // Log the error for debugging
      console.log("[PaymentsActivationBanner] FETCH EXCEPTION:", err.message);
      // Reset fetchedRef to allow retry on next mount/re-render
      fetchedRef.current = false;
      setActivationStatus(null);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);
  
  // Initial fetch - once per session (or retry on failure)
  useEffect(() => {
    console.log("[PaymentsActivationBanner] MOUNT/UPDATE: useEffect triggered", {
      fetchedRef: fetchedRef.current,
      hasActivationStatus: activationStatus !== null,
      tenantId,
    });
    
    // Skip if already successfully fetched
    if (fetchedRef.current && activationStatus !== null) {
      console.log("[PaymentsActivationBanner] FETCH SKIPPED: already fetched");
      return;
    }
    // P0 FIX: Removed isAdminOrOwner check - backend handles authorization
    if (!tenantId) {
      console.log("[PaymentsActivationBanner] FETCH SKIPPED: no tenantId in useEffect");
      return;
    }
    
    fetchActivationStatus();
  }, [tenantId, fetchActivationStatus, activationStatus]);
  
  // =========================================================================
  // CTA HANDLERS
  // =========================================================================
  
  /**
   * Handle "Continue setup" CTA for IN_PROGRESS state
   * Calls POST /api/payments/finix/onboarding-link (idempotent)
   * Opens onboarding_url in new tab, then refetches status
   */
  const handleContinueSetup = async () => {
    setCtaLoading(true);
    setCtaError(null);
    try {
      // P0 FIX: Include Bearer token for auth (same pattern as Dashboard.js, AppLayout.js)
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/payments/finix/onboarding-link`, {
        method: "POST",
        credentials: "include",
        headers: { 
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      
      // Check if response is JSON
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        // Non-JSON response (likely Cloudflare HTML error page)
        const statusText = response.status === 521 ? "Server down" : 
                           response.status === 502 ? "Bad gateway" :
                           response.status === 503 ? "Service unavailable" :
                           `HTTP ${response.status}`;
        console.error("[PaymentsActivationBanner] Non-JSON response:", response.status, statusText);
        setCtaError(`Unable to connect to payment service (${statusText}). Please try again.`);
        return;
      }
      
      const data = await response.json();
      
      if (data.onboarding_url) {
        // Open in new tab
        window.open(data.onboarding_url, "_blank", "noopener,noreferrer");
        setCtaError(null);
      } else if (data.ok === false) {
        // Backend returned error - show to user
        const userMessage = data.message || "Unable to continue setup. Please try again.";
        console.warn("[PaymentsActivationBanner] Onboarding link failed:", data.message, data.error_id);
        setCtaError(userMessage);
      }
      
      // Refetch status after CTA action
      await fetchActivationStatus();
    } catch (err) {
      console.error("[PaymentsActivationBanner] CTA error:", err);
      setCtaError("Connection error. Please check your internet and try again.");
    } finally {
      setCtaLoading(false);
    }
  };
  
  /**
   * Handle "Activate online payments" CTA for APPROVED_UNPAID state
   * Deep links to Upgrades with Payment Processing preselected and checkout modal open
   */
  const handleActivatePayments = () => {
    // Check if already on upgrades page
    if (location.pathname === "/settings/upgrades") {
      // Already on upgrades - dispatch custom event to trigger auto-checkout
      window.dispatchEvent(new CustomEvent("fsp:autocheckout", { 
        detail: { feature: "payment_processing" } 
      }));
    } else {
      // Navigate with autocheckout param
      navigate("/settings/upgrades?autocheckout=payment_processing");
    }
  };
  
  /**
   * Handle "Remind me later" - snooze for 15 MINUTES only (short TTL)
   * Banner will automatically return after snooze expires
   * There is NO permanent dismiss option
   */
  const handleSnooze = () => {
    setSnooze(tenantId);
    setIsHidden(true);
  };
  
  /**
   * Handle "Learn more" link
   */
  const handleLearnMore = () => {
    navigate("/settings?tab=payments");
  };
  
  // =========================================================================
  // RENDER
  // =========================================================================
  
  // P0 DIAGNOSTIC: Log render decision
  const showBannerResult = shouldShowBanner();
  console.log("[PaymentsActivationBanner] RENDER DECISION:", {
    isLoading,
    shouldShowBanner: showBannerResult,
    willRender: !isLoading && showBannerResult,
  });
  
  // Don't render if loading or shouldn't show
  if (isLoading) {
    console.log("[PaymentsActivationBanner] RENDER RETURN NULL: isLoading=true");
    return null;
  }
  if (!showBannerResult) {
    console.log("[PaymentsActivationBanner] RENDER RETURN NULL: shouldShowBanner()=false");
    return null;
  }
  
  const state = activationStatus?.state;
  const nextStepAction = activationStatus?.next_step?.action;
  
  // Determine CTA text and handler based on backend-computed next_step.action
  // This is the deterministic, backend-driven approach
  const getCTAConfig = () => {
    if (nextStepAction === "resume_onboarding") {
      return {
        ctaText: "Finish application",
        ctaHandler: handleContinueSetup,
      };
    } else if (nextStepAction === "pay_now") {
      return {
        ctaText: "Pay now",
        ctaHandler: handleActivatePayments,  // Routes to purchase/subscription flow
      };
    } else if (nextStepAction === "contact_support") {
      return {
        ctaText: "Contact support",
        ctaHandler: () => {
          // Open support contact
          window.location.href = "/settings?tab=support";
        },
      };
    } else if (nextStepAction === "wait_for_review") {
      // Submitted, waiting for approval - no CTA needed
      return {
        ctaText: null,
        ctaHandler: null,
        noCta: true,
      };
    }
    // "complete" or unknown - no CTA needed
    return null;
  };
  
  const ctaConfig = getCTAConfig();
  
  // Banner visual config based on state
  const bannerConfig = {
    IN_PROGRESS: {
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      iconColor: "text-amber-600",
      Icon: CreditCard,
      title: activationStatus?.next_step?.title || "Finish your payments application",
      body: activationStatus?.next_step?.body || "You're almost done — complete setup to accept deposits and card payments.",
    },
    APPROVED_UNPAID: {
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200",
      iconColor: "text-emerald-600",
      Icon: CheckCircle,
      title: activationStatus?.next_step?.title || "You're approved 🎉",
      body: activationStatus?.next_step?.body || "Activate online payments to start taking deposits and getting paid faster.",
    },
    APPROVED_MERCHANT_MISSING: {
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      iconColor: "text-amber-600",
      Icon: CreditCard,
      title: activationStatus?.next_step?.title || "Finish Payments Setup",
      body: activationStatus?.next_step?.body || "We're still finalizing your merchant setup. Please wait or contact support if this persists.",
    },
  };
  
  const config = bannerConfig[state];
  if (!config) {
    console.log("[PaymentsActivationBanner] RENDER RETURN NULL: no bannerConfig for state =", state);
    return null;
  }
  
  // If ctaConfig is null (not even a wait_for_review case), hide banner
  // If ctaConfig.noCta is true (wait_for_review), show banner without CTA
  if (!ctaConfig && !activationStatus?.in_progress_sub_state) {
    console.log("[PaymentsActivationBanner] RENDER RETURN NULL: no ctaConfig and no in_progress_sub_state", {
      ctaConfig,
      in_progress_sub_state: activationStatus?.in_progress_sub_state,
      nextStepAction,
    });
    return null;
  }
  
  const { bgColor, borderColor, iconColor, Icon, title, body } = config;
  const showCta = ctaConfig && !ctaConfig.noCta && ctaConfig.ctaText;
  
  // P0 DIAGNOSTIC: SUCCESS - Banner will render
  console.log("[PaymentsActivationBanner] SUCCESS - RENDERING BANNER:", {
    state,
    in_progress_sub_state: activationStatus?.in_progress_sub_state,
    nextStepAction,
    ctaText: ctaConfig?.ctaText,
    showCta,
    title,
  });
  
  return (
    <div 
      className={`${bgColor} ${borderColor} border-b px-4 py-3`}
      role="banner"
      aria-live="polite"
      data-testid="payments-activation-banner"
      data-state={state}
      data-sub-state={activationStatus?.in_progress_sub_state || "none"}
    >
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
        {/* Icon */}
        <div className={`${iconColor} flex-shrink-0`}>
          <Icon size={24} weight="fill" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-zinc-900 text-sm" data-testid="banner-title">
            {title}
          </p>
          <p className="text-zinc-600 text-sm mt-0.5" data-testid="banner-body">
            {body}
          </p>
          {/* Error message display */}
          {ctaError && (
            <p className="text-red-600 text-sm mt-1 font-medium" data-testid="banner-error">
              ⚠️ {ctaError}
            </p>
          )}
        </div>
        
        {/* Actions - Only show if there's a CTA */}
        {showCta && (
          <div className="flex items-center gap-3 flex-shrink-0 w-full sm:w-auto">
            {/* Primary CTA */}
            <Button
              onClick={ctaConfig.ctaHandler}
              disabled={ctaLoading}
              size="sm"
              className="bg-[#FF6600] hover:bg-[#E65C00] text-white gap-1.5"
              data-testid="banner-cta"
            >
              {ctaLoading ? (
                <span className="flex items-center gap-1.5">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </span>
              ) : (
                <>
                  <CreditCard size={16} />
                  {ctaConfig.ctaText}
                </>
              )}
            </Button>
            
            {/* Snooze button - 15 minute TTL, returns automatically */}
            <button
              onClick={handleSnooze}
              className="text-zinc-500 hover:text-zinc-700 text-sm underline"
              data-testid="banner-snooze"
            >
              Remind me later
            </button>
            
            {/* Learn more link */}
            <button
              onClick={handleLearnMore}
              className="text-zinc-500 hover:text-zinc-700 text-sm hidden sm:block"
              data-testid="banner-learn-more"
            >
              Learn more
            </button>
          </div>
        )}
        
        {/* REMOVED: Close (X) button - contractors cannot permanently dismiss banner
         * Banner uses 15-minute snooze only, which auto-expires and banner returns
         * This ensures contractors are frequently reminded to complete onboarding
         */}
      </div>
    </div>
  );
}
